// angular
import { Component, effect, inject, input, OnInit } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
// components
import { InputComponent } from '../input/input.component';
import { ButtonComponent } from '../button/button.component';
import { RegisterButtonComponent } from '../register-button/register-button.component';
// services
import { UserService } from '@app/shared/services/user.service';
import { CommentsService } from '@app/core/shared/services/comments.service';
import { RecaptchaService } from '@app/shared/services/recaptcha.service';
// interfaces & types & env
import { ICommentForm } from '@app/shared/interfaces/comment.interface';
import { RecaptchaData } from '@app/shared/types/recaptcha-data.type';
import { IUser } from '@app/shared/types/user.type';
import { environment } from '@environments/environment';

@Component({
  selector: 'pwj-comment-form-box',
  standalone: true,
  imports: [
    NgOptimizedImage,
    TranslateModule,
    ReactiveFormsModule,
    InputComponent,
    ButtonComponent,
    RegisterButtonComponent,
  ],
  templateUrl: './comment-form-box.component.html',
  styleUrl: './comment-form-box.component.scss',
})
export class CommentFormBoxComponent {
  public panelUrl: string = environment.baseUrl.panelUrl;
  // form property
  public commentForm!: FormGroup;
  public userData!: IUser;
  public replayComment!: string | null;
  public formIsSubmitting: boolean = false;
  public isDisabledSubmitBtn: boolean = false;
  public isUserLoggedIn!: boolean;

  // inputs
  pageName = input.required<string>();
  pageId = input.required<string>();

  // dependencies
  private readonly _fb = inject(FormBuilder);
  private readonly _userService = inject(UserService);
  private readonly _commentService = inject(CommentsService);
  private readonly _recaptchaService = inject(RecaptchaService);

  constructor() {
    this._buildForm();

    this._recaptchaService.recaptchaValidation('comment_form');

    // checked user logged-in or no
    effect(() => {
      this.isUserLoggedIn =
        (this._userService.userSignal()?.roles?.length || 0) > 0 && !!this._userService.userSignal()?.mobile_verified;
    });

    // get user data
    this._userService.user$.subscribe(user => {
      this.userData = user;
    });

    effect(() => {
      // get comment replay
      this.replayComment = this._commentService.commentReplay()?.user as string;
    });

    effect(() => {
      if (this._recaptchaService.recaptchaData() !== null) {
        const captchaData =
          this._recaptchaService.recaptchaData() as RecaptchaData;
        if (captchaData?.score < environment.recaptcha.targetScore) {
          this.isDisabledSubmitBtn = true;
        }
      }
    });

    this.commentForm.valueChanges.subscribe(_ => {
      if (!this.commentForm.dirty) {
        this.isDisabledSubmitBtn = true;
      }
    });
  }

  // build comment form
  private _buildForm() {
    this.commentForm = this._fb.group({
      comment: new FormControl(''),
    });
  }

  public submitComment() {
    // check page id & page name is valid
    if (this.pageId() && this.pageName) {
      // make ready comment data
      const commentData: ICommentForm = {
        text: this.commentForm.value.comment,
        [this.pageName()]: this.pageId(),
      };
      // submit comment
      this._commentService.sendCommentForm(commentData);
      this.commentForm.reset();
    }
  }

  public onCloseReplay() {
    // empty comment replay subject
    this._commentService.commentReplay.set({
      id: null,
      user: null,
    });
  }
}
